// Accounts list page — full-text search, multi-select filters, column toggles,
// CSV/XLSX export of the current filtered view.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardBody } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { accounts, customers, lookups, testers } from '@/lib/api';
import { formatBalance } from '@/lib/format';
import { useMe } from '@/hooks/useMe';
import type {
  AccountListItem,
  AccountListQuery,
  AccountStatus,
  TillerStatus,
} from '@frt/shared-types';
import { csvCell, formatPhone } from '@frt/shared-types';

// Column order: FRT Customer + Category lead the row (per ops feedback —
// Category is a customer-level attribute now). display_name follows.
//
// `sortKey` names the underlying AccountListItem field used for sorting.
// Most columns map 1:1 to a single field; `last_update` is rendered from
// `last_update_date` so sorting follows that ISO date string.
// `kind` drives the comparator (string | number | date | bool).
type ColKind = 'string' | 'number' | 'date' | 'bool';
const ALL_COLUMNS: ReadonlyArray<{
  key: string;
  label: string;
  sortKey: keyof AccountListItem;
  kind: ColKind;
}> = [
  { key: 'frt_customer_code', label: 'Customer', sortKey: 'frt_customer_code', kind: 'string' },
  { key: 'frt_category_name', label: 'Category', sortKey: 'frt_category_name', kind: 'string' },
  { key: 'display_name', label: 'Display name', sortKey: 'display_name', kind: 'string' },
  { key: 'tester_display', label: 'Tester', sortKey: 'tester_display', kind: 'string' },
  { key: 'product_type_code', label: 'Product', sortKey: 'product_type_code', kind: 'string' },
  { key: 'account_type_code', label: 'Type', sortKey: 'account_type_code', kind: 'string' },
  { key: 'tiller_status', label: 'Tiller', sortKey: 'tiller_status', kind: 'string' },
  { key: 'account_status', label: 'Status', sortKey: 'account_status', kind: 'string' },
  { key: 'on_point_tester_display', label: 'On point', sortKey: 'on_point_tester_display', kind: 'string' },
  { key: 'latest_balance_amount', label: 'Latest balance', sortKey: 'latest_balance_amount', kind: 'number' },
  { key: 'last_update', label: 'Last Update', sortKey: 'last_update_date', kind: 'date' },
  { key: 'date_opened', label: 'Date Opened', sortKey: 'date_opened', kind: 'date' },
  { key: 'debit_card_available', label: 'Debit Card Available', sortKey: 'debit_card_available', kind: 'bool' },
  { key: 'checks_available', label: 'Checks Available', sortKey: 'checks_available', kind: 'bool' },
  { key: 'in_bitwarden', label: 'In Bitwarden', sortKey: 'in_bitwarden', kind: 'bool' },
  { key: 'latest_balance_as_of', label: 'As of', sortKey: 'latest_balance_as_of', kind: 'date' },
  // mig 0017 — customer profile; hidden by default
  { key: 'platform_name', label: 'Platform', sortKey: 'platform_name', kind: 'string' },
  { key: 'platform_type_name', label: 'Platform Type', sortKey: 'platform_type_name', kind: 'string' },
  { key: 'customer_profile_display', label: 'Online Profile', sortKey: 'customer_profile_display', kind: 'string' },
  // mig 0013 (from the customer profile since 0017) — hidden by default; toggle on for exports
  { key: 'phone_on_file', label: 'Phone on File', sortKey: 'phone_on_file_number', kind: 'string' },
  { key: 'email_on_file', label: 'Email on File', sortKey: 'email_on_file', kind: 'string' },
  { key: 'oob_summary', label: 'OOB Channels', sortKey: 'oob_summary', kind: 'string' },
  // mig 0014 — hidden by default
  { key: 'linked_accounts_summary', label: 'Linked Accounts', sortKey: 'linked_accounts_summary', kind: 'string' },
  { key: 'previously_linked_summary', label: 'Previously Linked', sortKey: 'previously_linked_summary', kind: 'string' },
] as const;

type ColKey = (typeof ALL_COLUMNS)[number]['key'];

export function AccountsListPage() {
  const { data: me } = useMe();
  const canEdit = me?.role === 'admin' || me?.role === 'senior';

  const search = useSearch({ strict: false }) as { ids?: string };
  const navigate = useNavigate();

  const [query, setQuery] = useState<AccountListQuery>(() => ({
    per_page: 200,
    ...(search.ids ? { ids: search.ids } : {}),
  }));
  // Keep the query in sync if the user re-enters the page from a different
  // deep-link (e.g. another alert "Review" click).
  useEffect(() => {
    setQuery((q) => {
      if (search.ids === q.ids) return q;
      return { ...q, ids: search.ids };
    });
  }, [search.ids]);
  const [searchInput, setSearchInput] = useState('');
  const [visibleCols, setVisibleCols] = useState<Set<ColKey>>(
    new Set([
      'frt_customer_code',
      'frt_category_name',
      'display_name',
      'tester_display',
      'product_type_code',
      'tiller_status',
      'account_status',
      'latest_balance_amount',
      'last_update',
      'date_opened',
      'debit_card_available',
      'checks_available',
      'in_bitwarden',
    ]),
  );
  const [sortField, setSortField] = useState<ColKey>('display_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const { data: customersList } = useQuery({
    queryKey: ['customers', { per_page: 500 }],
    queryFn: () => customers.list({ per_page: 500 }),
  });
  const { data: testersList } = useQuery({
    queryKey: ['testers', { per_page: 500 }],
    queryFn: () => testers.list({ per_page: 500 }),
  });
  const { data: categoriesList } = useQuery({
    queryKey: ['lookups', 'frt-categories'],
    queryFn: () => lookups.frtCategories.list(),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts', query],
    queryFn: () => accounts.list(query),
  });

  const visibleColsArr = useMemo(
    () => ALL_COLUMNS.filter((c) => visibleCols.has(c.key)),
    [visibleCols],
  );

  // Client-side sort over the current page (per_page=200 in our default
  // query). If the dataset ever exceeds the page size, sort would need to
  // move server-side via an `order` query param.
  const sortedRows = useMemo(() => {
    const rows = data?.data ?? [];
    const col = ALL_COLUMNS.find((c) => c.key === sortField);
    if (!col) return rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[col.sortKey];
      const bv = (b as unknown as Record<string, unknown>)[col.sortKey];
      // null/undefined always sorts last regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (col.kind === 'number') return ((av as number) - (bv as number)) * dir;
      if (col.kind === 'bool') return (boolRank(av) - boolRank(bv)) * dir;
      // string + date (ISO YYYY-MM-DD sorts lexicographically)
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [data, sortField, sortDir]);

  const onSort = (field: ColKey) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      const col = ALL_COLUMNS.find((c) => c.key === field);
      setSortField(field);
      // Numbers, dates, and booleans default to descending (largest/newest/
      // "Yes" first); strings default to ascending (A→Z).
      setSortDir(col && col.kind !== 'string' ? 'desc' : 'asc');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Accounts</h1>
        {canEdit && (
          <Link to="/accounts/new">
            <Button>New Account</Button>
          </Link>
        )}
      </div>

      <Card>
        <CardBody className="space-y-3">
          <form
            className="flex flex-wrap gap-3 items-end"
            onSubmit={(e) => {
              e.preventDefault();
              setQuery((q) => ({ ...q, q: searchInput || undefined }));
            }}
          >
            <label className="block text-sm flex-1 min-w-[14rem]">
              <span className="block text-slate-600">Search display_name</span>
              <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">Tiller status</span>
              <select
                value={query.tiller_status ?? ''}
                onChange={(e) =>
                  setQuery((q) => ({
                    ...q,
                    tiller_status: (e.target.value as TillerStatus) || undefined,
                  }))
                }
                className="rounded border border-slate-300 px-2 py-1.5"
              >
                <option value="">All</option>
                <option value="integrated">Integrated</option>
                <option value="login_issues">Login issues</option>
                <option value="manual">Manual</option>
                <option value="new">New</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">Account status</span>
              <select
                value={query.account_status ?? ''}
                onChange={(e) =>
                  setQuery((q) => ({
                    ...q,
                    account_status: (e.target.value as AccountStatus) || undefined,
                  }))
                }
                className="rounded border border-slate-300 px-2 py-1.5"
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="suppressed">Suppressed</option>
                <option value="closed">Closed</option>
                <option value="converted_personal">Converted personal</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">Customer</span>
              <select
                value={query.frt_customer_id ?? ''}
                onChange={(e) =>
                  setQuery((q) => ({
                    ...q,
                    frt_customer_id: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
                className="rounded border border-slate-300 px-2 py-1.5"
              >
                <option value="">All</option>
                {(customersList?.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">Tester</span>
              <select
                value={query.tester_id ?? ''}
                onChange={(e) =>
                  setQuery((q) => ({
                    ...q,
                    tester_id: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
                className="rounded border border-slate-300 px-2 py-1.5"
              >
                <option value="">All</option>
                {(testersList?.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.short_name ?? t.short_business_name ?? `tester#${t.id}`}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">Category</span>
              <select
                value={query.frt_category_id ?? ''}
                onChange={(e) =>
                  setQuery((q) => ({
                    ...q,
                    frt_category_id: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
                className="rounded border border-slate-300 px-2 py-1.5"
              >
                <option value="">All</option>
                {(categoriesList?.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <Button type="submit">Apply</Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setQuery({ per_page: 200 });
                setSearchInput('');
                if (search.ids) {
                  void navigate({ to: '/accounts', search: {} });
                }
              }}
            >
              Reset
            </Button>
          </form>
          {search.ids && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <strong className="font-semibold">Filtered to {search.ids.split(',').length}{' '}
              account{search.ids.split(',').length === 1 ? '' : 's'}</strong> from the dashboard
              alert. Click <em>Reset</em> to view all accounts.
            </div>
          )}
          <details className="text-sm">
            <summary className="cursor-pointer text-slate-600">Columns</summary>
            <div className="flex flex-wrap gap-2 pt-2">
              {ALL_COLUMNS.map((c) => (
                <label key={c.key} className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={visibleCols.has(c.key)}
                    onChange={(e) =>
                      setVisibleCols((s) => {
                        const n = new Set(s);
                        if (e.target.checked) n.add(c.key);
                        else n.delete(c.key);
                        return n;
                      })
                    }
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </details>
          <div className="flex gap-2 text-sm">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => exportCsv(sortedRows, visibleColsArr)}
              disabled={!data}
            >
              Export CSV
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => exportXlsx(sortedRows, visibleColsArr)}
              disabled={!data}
            >
              Export XLSX
            </Button>
            <span className="text-slate-500">
              {data ? `${data.total} accounts` : ''}
            </span>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
          {isLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500">
                  <tr>
                    <th className="py-1 pr-3 whitespace-nowrap text-right w-12 font-mono">
                      #
                    </th>
                    {visibleColsArr.map((c) => {
                      const active = sortField === c.key;
                      const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '↕';
                      return (
                        <th key={c.key} className="py-1 pr-3 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => onSort(c.key)}
                            className={`cursor-pointer select-none hover:text-slate-900 ${
                              active ? 'text-slate-900' : ''
                            }`}
                          >
                            {c.label}
                            <span
                              className={`ml-1 ${active ? 'text-slate-700' : 'text-slate-300'}`}
                            >
                              {arrow}
                            </span>
                          </button>
                        </th>
                      );
                    })}
                    {canEdit && <th />}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((a, idx) => (
                    <tr key={a.id} className="border-t border-slate-100">
                      <td className="py-1 pr-3 text-right text-slate-500 font-mono">
                        {idx + 1}
                      </td>
                      {visibleColsArr.map((c) => (
                        <td
                          key={c.key}
                          className={`py-1 pr-3 whitespace-nowrap ${
                            c.key === 'latest_balance_amount' ? 'font-mono text-right' : ''
                          } ${
                            c.key === 'last_update' ? lastUpdateBgClass(a.last_update_date) : ''
                          }`}
                        >
                          {c.key === 'display_name' ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Link
                                to="/accounts/$id"
                                params={{ id: String(a.id) }}
                                className="text-brand-700 hover:underline font-mono"
                              >
                                {a.display_name}
                              </Link>
                              {a.needs_renewal && (
                                <span
                                  className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700"
                                  title="Contract expired — open Tester Compensation to resolve"
                                >
                                  Action Required
                                </span>
                              )}
                              {a.payment_status_name === 'Closing' && (
                                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                                  Pending Closing
                                </span>
                              )}
                              {a.payment_status_name === 'Suspended' && (
                                <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                                  Suspended
                                </span>
                              )}
                            </span>
                          ) : c.key === 'latest_balance_amount' ? (
                            formatBalance(a.latest_balance_amount)
                          ) : c.key === 'last_update' ? (
                            formatLastUpdate(a.last_update_date)
                          ) : c.key === 'phone_on_file' ? (
                            formatPhone(a.phone_on_file_country_code, a.phone_on_file_number) || '—'
                          ) : c.kind === 'bool' ? (
                            formatYesNo(
                              (a as unknown as Record<string, unknown>)[c.sortKey] as
                                | boolean
                                | null,
                            )
                          ) : (
                            String(
                              (a as unknown as Record<string, unknown>)[c.sortKey] ?? '—',
                            )
                          )}
                        </td>
                      ))}
                      {canEdit && (
                        <td className="py-1 pr-3 text-right whitespace-nowrap">
                          <SuppressAccountButton
                            id={a.id}
                            status={a.account_status}
                            adminOnly={me?.role === 'admin'}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// CSV / XLSX export — both implemented locally without an extra dependency.
// XLSX uses the SpreadsheetML 2003 single-sheet format for simplicity (Excel
// opens it natively and we don't have to ship a binary builder).

// "Last Update" rendering. Diff is calendar days between today (UTC) and the
// stored YYYY-MM-DD. Color thresholds per ops feedback:
//   diff = 0           → "Today"           green
//   1 ≤ diff ≤ 7      → "N days"          green
//   8 ≤ diff ≤ 20     → "N days"          orange
//   diff > 20          → "N days"          red
//   no balance yet     → "—"               no color
function diffDays(dateOnly: string | null): number | null {
  if (!dateOnly) return null;
  const m = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const then = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.floor((today - then) / 86_400_000);
}

function formatLastUpdate(dateOnly: string | null): string {
  const d = diffDays(dateOnly);
  if (d === null) return '—';
  if (d <= 0) return 'Today';
  return `${d} days`;
}

function formatYesNo(v: boolean | null | undefined): string {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return '—';
}

// Sort rank for nullable booleans: true > false > (null already handled
// by the comparator so it ranks last regardless).
function boolRank(v: unknown): number {
  if (v === true) return 1;
  if (v === false) return 0;
  return 0;
}

function lastUpdateBgClass(dateOnly: string | null): string {
  const d = diffDays(dateOnly);
  if (d === null) return '';
  if (d <= 7) return 'bg-green-100 text-green-900';
  if (d <= 20) return 'bg-amber-100 text-amber-900';
  return 'bg-red-100 text-red-900';
}

function escapeCsv(v: unknown): string {
  return csvCell(v);
}

/** Get the export-time value for a row+column. Most columns are direct
 *  property reads; a few are computed/composed (e.g. "Last Update" derives
 *  from last_update_date) and tri-state booleans render as Yes/No/—. */
function exportValue(r: AccountListItem, key: ColKey): unknown {
  if (key === 'last_update') return formatLastUpdate(r.last_update_date);
  if (key === 'phone_on_file') return formatPhone(r.phone_on_file_country_code, r.phone_on_file_number);
  const col = ALL_COLUMNS.find((c) => c.key === key);
  const raw = col
    ? (r as unknown as Record<string, unknown>)[col.sortKey]
    : (r as unknown as Record<string, unknown>)[key];
  if (col?.kind === 'bool') return formatYesNo(raw as boolean | null);
  return raw;
}

function exportCsv(
  rows: AccountListItem[],
  cols: { key: ColKey; label: string }[],
): void {
  const header = ['#', ...cols.map((c) => c.label)].join(',');
  const lines = rows.map((r, idx) =>
    [
      String(idx + 1),
      ...cols.map((c) => escapeCsv(exportValue(r, c.key))),
    ].join(','),
  );
  const csv = [header, ...lines].join('\n');
  download(`accounts-${new Date().toISOString().slice(0, 10)}.csv`, csv, 'text/csv');
}

function exportXlsx(
  rows: AccountListItem[],
  cols: { key: ColKey; label: string }[],
): void {
  // SpreadsheetML 2003 single-sheet format: Excel-compatible, no deps needed.
  const escape = (v: unknown): string =>
    String(v ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  const headerCells =
    `<Cell><Data ss:Type="String">#</Data></Cell>` +
    cols
      .map((c) => `<Cell><Data ss:Type="String">${escape(c.label)}</Data></Cell>`)
      .join('');
  const headerXml = `<Row>${headerCells}</Row>`;
  const rowXml = rows
    .map((r, idx) => {
      const lineCell = `<Cell><Data ss:Type="Number">${idx + 1}</Data></Cell>`;
      const valueCells = cols
        .map((c) => {
          const v = exportValue(r, c.key);
          const isNum = typeof v === 'number';
          return `<Cell><Data ss:Type="${isNum ? 'Number' : 'String'}">${escape(v)}</Data></Cell>`;
        })
        .join('');
      return `<Row>${lineCell}${valueCells}</Row>`;
    })
    .join('');
  const xml =
    `<?xml version="1.0"?>` +
    `<?mso-application progid="Excel.Sheet"?>` +
    `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">` +
    `<Worksheet ss:Name="Accounts"><Table>${headerXml}${rowXml}</Table></Worksheet>` +
    `</Workbook>`;
  download(
    `accounts-${new Date().toISOString().slice(0, 10)}.xls`,
    xml,
    'application/vnd.ms-excel',
  );
}

function download(name: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Per-row Suppress action. Suppression flips account_status away from
// 'active'; suppressed accounts are excluded from all reports + downloads
// (custom report builder defaults a.account_status='active' unless caller
// explicitly opts in). Restore is admin-only per docs/03; keep the same
// gate here.
function SuppressAccountButton({
  id,
  status,
  adminOnly,
}: {
  id: number;
  status: AccountStatus;
  adminOnly: boolean;
}): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const isActive = status === 'active';
  const action = useMutation({
    mutationFn: () =>
      isActive ? accounts.suppress(id, { reason: null }) : accounts.restore(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['account', id] });
    },
  });
  if (!isActive && !adminOnly) return null;
  return (
    <Button
      size="sm"
      variant={isActive ? 'destructive' : 'secondary'}
      onClick={() => {
        if (
          isActive &&
          !confirm(
            'Suppress this account? It will be hidden from reports, dashboards, and exports.',
          )
        ) {
          return;
        }
        action.mutate();
      }}
      disabled={action.isPending}
    >
      {isActive ? 'Suppress' : 'Restore'}
    </Button>
  );
}
